import React from "react";
import { incrementUpvote, decrementUpvote, hasUserVoted } from "../api/upvotes";
import { fetchComments, fetchCommentCounts, postComment } from "../api/fetchComments";
import { supabase } from "../lib/supabaseClient";
import LetterAvatar from "./LetterAvatar";
import AuthPopup from "./AuthPopup";
import ProfileSetupModal from "./ProfileSetupModal";
import { usePostHog } from 'posthog-js/react';
import "./TableView.css";

export default function TableView({
  filteredRecords,
  formatDate,
  sortField,
  sortDirection,
  handleSort,
  sortMode,
}) {
  // Local auth state management
  const [user, setUser] = React.useState(null);
  const [userProfile, setUserProfile] = React.useState(null);
  
  // Initialize auth state
  React.useEffect(() => {
    const initAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const currentUser = session?.user || null;
        setUser(currentUser);
        
        if (currentUser) {
          // Fetch user profile
          const { data } = await supabase
            .from('users')
            .select('*')
            .eq('id', currentUser.id)
            .single();
          setUserProfile(data);
        }
      } catch (error) {
        console.error('Auth init error:', error);
      }
    };
    
    initAuth();
    
    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      const currentUser = session?.user || null;
      setUser(currentUser);
      
      if (currentUser) {
        const { data } = await supabase
          .from('users')
          .select('*')
          .eq('id', currentUser.id)
          .single();
        setUserProfile(data);
      } else {
        setUserProfile(null);
      }
    });
    
    return () => subscription.unsubscribe();
  }, []);
  
  // Auth functions
  const signInWithEmail = async (email) => {
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: window.location.origin
        }
      });
      
      if (error) throw error;
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };
  
  const updateUserProfile = async (profileData) => {
    if (!user) return { success: false, error: 'No user logged in' };

    try {
      const { error } = await supabase
        .from('users')
        .upsert({
          id: user.id,
          ...profileData,
          updated_at: new Date().toISOString()
        });

      if (error) throw error;

      // Refresh the profile
      const { data } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single();
      setUserProfile(data);
      
      return { success: true };
    } catch (error) {
      console.error('Error updating profile:', error);
      return { success: false, error: error.message };
    }
  };
  
  // Real voting state - now using data from recommendations
  const [votes, setVotes] = React.useState({});
  const [animatingItems, setAnimatingItems] = React.useState(new Set());
  const posthog = usePostHog();
  
  // Comments state
  const [commentCounts, setCommentCounts] = React.useState({});
  const [comments, setComments] = React.useState({});
  
  // Auth popup state
  const [showAuthPopup, setShowAuthPopup] = React.useState(false);
  
  // Profile setup modal state
  const [showProfileSetup, setShowProfileSetup] = React.useState(false);
  
  // Comment input states - one per recommendation
  const [commentInputs, setCommentInputs] = React.useState({});
  const [commentSubmitting, setCommentSubmitting] = React.useState({});
  
  
  // Initialize vote state from recommendation data (no loading needed)
  React.useEffect(() => {
    if (filteredRecords.length === 0) return;
    
    // Initialize vote state with data from recommendations
    const initialVotes = {};
    filteredRecords.forEach(record => {
      const recommendationId = record.fields?.id;
      if (recommendationId) {
        initialVotes[record.id] = {
          upvotes: record.fields?.upvoteCount || 0, // Use upvote count from recommendation data
          userUpvoted: hasUserVoted(recommendationId),
          recommendationId: recommendationId
        };
      }
    });
    
    setVotes(initialVotes);
  }, [filteredRecords.map(r => r.id).join(',')]);  // Only re-run if record IDs change
  
  // Load comments and comment counts for all recommendations
  React.useEffect(() => {
    const loadCommentsData = async () => {
      if (filteredRecords.length === 0) return;
      
      const recommendationIds = filteredRecords
        .map(record => record.fields?.id)
        .filter(id => id != null);
      
      if (recommendationIds.length === 0) return;
      
      // Load comment counts
      const counts = await fetchCommentCounts(recommendationIds);
      setCommentCounts(counts);
      
      // Load comments for all recommendations
      const commentsData = {};
      await Promise.all(
        recommendationIds.map(async (id) => {
          const comments = await fetchComments(id);
          commentsData[id] = comments;
        })
      );
      setComments(commentsData);
    };
    
    loadCommentsData();
  }, [filteredRecords.map(r => r.fields?.id).join(',')]);  // Only re-run if recommendation IDs change
  
  const handleUpvote = async (recordId) => {
    const currentVote = votes[recordId];
    if (!currentVote || !currentVote.recommendationId) return;
    
    // Add animation class to the voted item
    setAnimatingItems(prev => new Set([...prev, recordId]));
    
    const wasUpvoted = currentVote.userUpvoted;
    const recommendationId = currentVote.recommendationId;
    
    try {
      let result;
      if (wasUpvoted) {
        // Remove upvote
        result = await decrementUpvote(recommendationId);
      } else {
        // Add upvote
        result = await incrementUpvote(recommendationId);
      }
      
      if (result) {
        // Track the vote action with PostHog
        if (posthog) {
          posthog.capture('recommendation_voted', {
            recommendation_id: recommendationId,
            action: wasUpvoted ? 'unvote' : 'vote',
            new_vote_count: result.total_count,
            user_id: user?.id || 'anonymous'
          });
        }

        // Update local state with new count
        setVotes(prev => ({
          ...prev,
          [recordId]: {
            ...currentVote,
            userUpvoted: !wasUpvoted,
            upvotes: result.total_count
          }
        }));
      } else {
        // If the API call failed, don't update the UI
        console.log('Vote update failed - user may have already voted or network error');
      }
    } catch (error) {
      console.error('Error updating vote:', error);
    }
    
    // Remove animation class after animation completes
    setTimeout(() => {
      setAnimatingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(recordId);
        return newSet;
      });
    }, 1000);
  };


  // Mock comment data - simplified without replies
  const getMockComments = (recordId) => [
    {
      id: `${recordId}-1`,
      author: "Alex Chen",
      content: "This is such a great recommendation! I listened to this episode and it completely changed my perspective.",
      timestamp: "2 hours ago"
    },
    {
      id: `${recordId}-2`,
      author: "Mike Rodriguez",
      content: "Has anyone found similar content to this? Looking for more in this vein.",
      timestamp: "4 hours ago"
    },
    {
      id: `${recordId}-3`,
      author: "Emma Johnson",
      content: "The host's interview style in this episode is incredible. Really knows how to ask the right questions.",
      timestamp: "6 hours ago"
    },
    {
      id: `${recordId}-4`,
      author: "David Park",
      content: "Yes! That's exactly what makes this podcast so good.",
      timestamp: "5 hours ago"
    }
  ];

  // Check if user needs to complete profile setup
  React.useEffect(() => {
    if (user && userProfile !== null && !userProfile?.name) {
      setShowProfileSetup(true);
    } else if (user && userProfile?.name) {
      setShowProfileSetup(false);
    }
  }, [user, userProfile]);

  // Handle comment input click for unauthenticated users or incomplete profiles
  const handleCommentClick = () => {
    if (!user) {
      setShowAuthPopup(true);
    } else if (!userProfile?.name) {
      setShowProfileSetup(true);
    }
  };

  const handleProfileSetupComplete = async (name) => {
    setShowProfileSetup(false);
    // Refresh the user profile to get the updated data
    if (user) {
      const { data } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single();
      setUserProfile(data);
    }
  };

  // Handle comment input changes
  const handleCommentInputChange = (recordId, value) => {
    setCommentInputs(prev => ({
      ...prev,
      [recordId]: value
    }));
  };

  // Handle comment submission
  const handleCommentSubmit = async (recordId, recommendationId) => {
    const commentText = commentInputs[recordId]?.trim();
    if (!commentText || !user || !userProfile?.name) return;

    setCommentSubmitting(prev => ({ ...prev, [recordId]: true }));

    try {
      const result = await postComment(recommendationId, commentText, user.id);
      
      if (result.success) {
        // Clear the input
        setCommentInputs(prev => ({
          ...prev,
          [recordId]: ''
        }));
        
        // Add the new comment to the local state immediately
        const newComment = {
          id: result.data.id,
          body: commentText,
          created_at: result.data.created_at,
          user_id: user.id,
          user_name: userProfile.name,
          recommendation_id: recommendationId
        };
        
        setComments(prev => ({
          ...prev,
          [recommendationId]: [...(prev[recommendationId] || []), newComment]
        }));
        
        // Update comment count
        setCommentCounts(prev => ({
          ...prev,
          [recommendationId]: (prev[recommendationId] || 0) + 1
        }));
      } else {
        console.error('Failed to post comment:', result.error);
        // Could show an error message to user here
      }
    } catch (error) {
      console.error('Error posting comment:', error);
    }

    setCommentSubmitting(prev => ({ ...prev, [recordId]: false }));
  };



  // Function to format date as relative time
  const formatRelativeDate = (dateString) => {
    if (!dateString) return "";
    
    let parsedDate = new Date(dateString);
    if (isNaN(parsedDate)) parsedDate = new Date(dateString.replace(/-/g, "/"));
    if (isNaN(parsedDate)) return "";
    
    const now = new Date();
    const diffInMs = now - parsedDate;
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) return "Today";
    if (diffInDays === 1) return "1 day ago";
    if (diffInDays < 7) return `${diffInDays} days ago`;
    if (diffInDays < 14) return "1 week ago";
    if (diffInDays < 21) return "2 weeks ago";
    if (diffInDays < 28) return "3 weeks ago";
    if (diffInDays < 60) return "1 month ago";
    if (diffInDays < 90) return "2 months ago";
    if (diffInDays < 365) {
      const months = Math.floor(diffInDays / 30);
      return `${months} months ago`;
    }
    const years = Math.floor(diffInDays / 365);
    return years === 1 ? "1 year ago" : `${years} years ago`;
  };

  // Sort records based on selected sort mode
  const sortedRecords = [...filteredRecords].sort((a, b) => {
    if (sortMode === 'best') {
      // Sort by upvote count (highest first)
      const aVotes = votes[a.id]?.upvotes || 0;
      const bVotes = votes[b.id]?.upvotes || 0;
      return bVotes - aVotes;
    } else {
      // Sort by date (newest first by default)
      const aDate = new Date(a.fields?.Date || 0);
      const bDate = new Date(b.fields?.Date || 0);
      return bDate - aDate;
    }
  });

  return (
    <div className="message-board">
      {sortedRecords.map((record, index) => {
        // Get real recommender data from the record
        const recommenderName = record.fields?.recommenderName;
        const recommenderId = record.fields?.recommenderId;
        const hasRecommender = recommenderName && recommenderId;
        
        const recordVotes = votes[record.id] || { upvotes: 0, userUpvoted: false };
        
        return (
          <div 
            key={record.id} 
            className={`message-board-item ${animatingItems.has(record.id) ? 'moving' : ''}`}
            style={{
              animationDelay: `${index * 0.05}s`
            }}
          >
            <div className="message-content">
              <div className="message-main">
                <h3 className="message-title">
                  {record.fields?.Recommendation || "Untitled"}
                </h3>
                {record.fields?.Link && (
                  <a
                    href={record.fields.Link}
                    className="message-link-icon"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open link"
                  >
                    <svg 
                      width="20" 
                      height="20" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    >
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                    </svg>
                  </a>
                )}
              </div>
              <div className="voting-section">
                <button 
                  className={`upvote-btn ${recordVotes.userUpvoted ? 'active' : ''}`}
                  onClick={() => handleUpvote(record.id)}
                  title={recordVotes.userUpvoted ? "Remove upvote" : "Upvote this recommendation"}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M7 14l5-5 5 5" />
                    <path d="M12 19V9" />
                  </svg>
                  <span className="upvote-count">
                    {recordVotes.upvotes}
                  </span>
                  <span className="upvote-label">
                    Votes
                  </span>
                </button>
              </div>
            </div>
            <div className="message-footer">
              {hasRecommender && (
                <div className="person-info">
                  <LetterAvatar name={recommenderName} className="person-avatar" />
                  <span className="person-name"><strong>@{recommenderName}</strong></span>
                </div>
              )}
              <span className="recommendation-date">{formatRelativeDate(record.fields?.Date)}</span>
            </div>
            
            <div className="comments-section-compact">
              {/* Display existing comments */}
              {comments[record.fields?.id] && comments[record.fields?.id].length > 0 && (
                <div className="comments-list">
                  {comments[record.fields?.id].map((comment) => (
                    <div key={comment.id} className="comment-item">
                      <div className="comment-content">
                        <p className="comment-text">{comment.body}</p>
                      </div>
                      <div className="comment-footer">
                        <div className="comment-author-info">
                          <LetterAvatar name={comment.user_name || 'Anonymous'} className="comment-avatar" />
                          <span className="comment-author-name">@{comment.user_name || 'Anonymous'}</span>
                        </div>
                        <span className="comment-date">{formatRelativeDate(comment.created_at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Add comment input */}
              <div className={`add-comment ${!user || !userProfile?.name ? 'unauthenticated' : ''}`}>
                {user && userProfile?.name ? (
                  <>
                    <input 
                      type="text" 
                      placeholder="What are your thoughts?" 
                      className="comment-input"
                      value={commentInputs[record.id] || ''}
                      onChange={(e) => handleCommentInputChange(record.id, e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleCommentSubmit(record.id, record.fields?.id);
                        }
                      }}
                      disabled={commentSubmitting[record.id]}
                    />
                    <button 
                      className="comment-submit"
                      onClick={() => handleCommentSubmit(record.id, record.fields?.id)}
                      disabled={commentSubmitting[record.id] || !commentInputs[record.id]?.trim()}
                    >
                      {commentSubmitting[record.id] ? 'POSTING...' : 'POST'}
                    </button>
                  </>
                ) : (
                  <>
                    <input 
                      type="text" 
                      placeholder={!user ? "Sign in to comment" : "Complete profile to comment"} 
                      className="comment-input"
                      onClick={handleCommentClick}
                      readOnly
                    />
                    <button 
                      className="comment-submit"
                      onClick={handleCommentClick}
                    >
                      {!user ? "SIGN IN" : "SETUP"}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })}
      
      {signInWithEmail && (
        <AuthPopup 
          isOpen={showAuthPopup}
          onClose={() => setShowAuthPopup(false)}
          onSignIn={signInWithEmail}
        />
      )}
      
      {user && updateUserProfile && (
        <ProfileSetupModal 
          isOpen={showProfileSetup}
          user={user}
          onComplete={handleProfileSetupComplete}
          updateUserProfile={updateUserProfile}
        />
      )}
    </div>
  );
}
